import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';

const CATEGORIES_BASE = {
  violation: { labelKey: 'violations', labelFallback: 'Violations', color: '#ef4444', icon: '🛡️' },
  session: { labelKey: 'liveSessionsCategory', labelFallback: 'LIVE Sessions', color: '#3b82f6', icon: '📡' },
  match: { labelKey: 'matchCategory', labelFallback: 'Match & Tournaments', color: '#a855f7', icon: '⚔️' },
  system: { labelKey: 'system', labelFallback: 'System', color: '#f97316', icon: '⚙️' },
  finance: { labelKey: 'financeCategory', labelFallback: 'Finance', color: '#22c55e', icon: '💰' },
};

const getCategoryFromTrigger = (trigger) => {
  if (trigger?.startsWith('violation')) return 'violation';
  if (trigger?.startsWith('session')) return 'session';
  if (trigger?.startsWith('match') || trigger?.startsWith('tournament')) return 'match';
  if (trigger?.startsWith('system') || trigger === 'contract_expiring') return 'system';
  if (trigger?.startsWith('finance')) return 'finance';
  return 'system';
};

const RECIPIENT_LABELS = {
  admin: 'Admin',
  manager: 'Manager',
  both: 'Admin + Manager',
  creator: 'Créateur',
};

const RECIPIENT_COLORS = {
  admin: { bg: 'rgba(59,130,246,0.15)', color: '#3B82F6' },
  manager: { bg: 'rgba(16,185,129,0.15)', color: '#10B981' },
  both: { bg: 'rgba(139,92,246,0.15)', color: '#8B5CF6' },
  creator: { bg: 'rgba(249,115,22,0.15)', color: '#f97316' },
};

const getParamDisplay = (rule) => {
  const p = rule?.trigger_params || {};
  if (p.days) return `${p.days} jours`;
  if (p.min_diamonds) return `≥ ${(p.min_diamonds / 1000).toFixed(0)}K 💎`;
  if (p.minutes_before) return `${p.minutes_before} min avant`;
  if (p.start_hour !== undefined) return `${p.start_hour}h → ${p.end_hour || 0}h UTC`;
  if (p.days_since_join) return `${p.days_since_join}j après inscription`;
  return '—';
};

const RulesTable = ({ rules, onEdit, onDelete, onToggle }) => {
  const { t } = useTranslation();
  const [deletingId, setDeletingId] = useState(null);
  const [filterCat, setFilterCat] = useState('all');

  const CATEGORIES = Object.fromEntries(
    Object.entries(CATEGORIES_BASE).map(([key, val]) => [key, { ...val, label: t(`admin.notificationsRules.${val.labelKey}`, val.labelFallback) }])
  );

  const handleToggle = async (rule) => {
    const { error } = await supabase
      ?.from('notification_rules')
      ?.update({ active: !rule?.active })
      ?.eq('id', rule?.id);
    if (!error) onToggle?.(rule?.id, !rule?.active);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette règle ?')) return;
    setDeletingId(id);
    await supabase?.from('notification_rules')?.delete()?.eq('id', id);
    onDelete?.(id);
    setDeletingId(null);
  };

  // Group by category
  const grouped = {};
  rules?.forEach((r) => {
    const cat = getCategoryFromTrigger(r?.trigger_type);
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(r);
  });

  const filteredCategories = filterCat === 'all'
    ? Object.keys(CATEGORIES).filter((c) => grouped[c]?.length)
    : [filterCat].filter((c) => grouped[c]?.length);

  const activeCount = rules?.filter((r) => r?.active)?.length || 0;
  const totalCount = rules?.length || 0;

  return (
    <div className="space-y-4">
      {/* Stats + filter */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
            {t('admin.notificationsRules.activeCount', '{{active}}/{{total}} active', { active: activeCount, total: totalCount })}
          </span>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <button
            onClick={() => setFilterCat('all')}
            className="px-2.5 py-1 rounded-lg text-xs font-medium transition-all"
            style={{
              backgroundColor: filterCat === 'all' ? '#E5202A' : '#1a1a1a',
              color: filterCat === 'all' ? '#fff' : 'rgba(255,255,255,0.5)',
              border: `1px solid ${filterCat === 'all' ? '#E5202A' : '#2a2a2a'}`,
            }}
          >
            {t('admin.notificationsRules.all', 'All')}
          </button>
          {Object.entries(CATEGORIES).map(([key, cat]) => (
            <button
              key={key}
              onClick={() => setFilterCat(key)}
              className="px-2.5 py-1 rounded-lg text-xs font-medium transition-all"
              style={{
                backgroundColor: filterCat === key ? cat.color : '#1a1a1a',
                color: filterCat === key ? '#fff' : 'rgba(255,255,255,0.5)',
                border: `1px solid ${filterCat === key ? cat.color : '#2a2a2a'}`,
              }}
            >
              {cat.icon} {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Rules by category */}
      {filteredCategories.map((catKey) => {
        const cat = CATEGORIES[catKey];
        const catRules = grouped[catKey] || [];
        return (
          <div key={catKey} className="rounded-xl border overflow-hidden" style={{ borderColor: '#1f1f1f' }}>
            {/* Category header */}
            <div className="px-4 py-2.5 flex items-center gap-2" style={{ backgroundColor: '#0d0d0d', borderBottom: '1px solid #1f1f1f' }}>
              <span>{cat.icon}</span>
              <span className="text-xs font-bold uppercase tracking-widest" style={{ color: cat.color, fontFamily: 'Sora, sans-serif' }}>
                {cat.label}
              </span>
              <span className="text-xs ml-auto" style={{ color: 'rgba(255,255,255,0.3)' }}>
                {catRules.filter((r) => r.active).length}/{catRules.length}
              </span>
            </div>

            {/* Rules */}
            {catRules.map((rule, idx) => {
              const recipientStyle = RECIPIENT_COLORS?.[rule?.recipient] || RECIPIENT_COLORS?.admin;
              return (
                <div
                  key={rule?.id}
                  className="px-4 py-3 flex items-center gap-3 flex-wrap md:flex-nowrap"
                  style={{
                    backgroundColor: idx % 2 === 0 ? '#111111' : '#0d0d0d',
                    borderBottom: idx < catRules.length - 1 ? '1px solid #1a1a1a' : 'none',
                    opacity: rule?.active ? 1 : 0.5,
                  }}
                >
                  {/* Toggle */}
                  <button
                    onClick={() => handleToggle(rule)}
                    className="relative inline-flex items-center shrink-0 w-10 h-5 rounded-full transition-colors duration-200"
                    style={{ backgroundColor: rule?.active ? '#22c55e' : '#374151' }}
                  >
                    <span
                      className="inline-block w-3.5 h-3.5 rounded-full bg-white shadow transition-transform duration-200"
                      style={{ transform: rule?.active ? 'translateX(21px)' : 'translateX(3px)' }}
                    />
                  </button>

                  {/* Name + template */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {rule?.name}
                    </p>
                    <p className="text-xs truncate mt-0.5" style={{ color: 'rgba(255,255,255,0.35)' }}>
                      {rule?.message_template}
                    </p>
                  </div>

                  {/* Params */}
                  <div className="hidden md:block shrink-0">
                    <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: '#1a1a1a', color: 'rgba(255,255,255,0.5)' }}>
                      {getParamDisplay(rule)}
                    </span>
                  </div>

                  {/* Recipient */}
                  <div className="shrink-0">
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium"
                      style={{ backgroundColor: recipientStyle?.bg, color: recipientStyle?.color }}>
                      {RECIPIENT_LABELS?.[rule?.recipient] || rule?.recipient}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      onClick={() => onEdit?.(rule)}
                      className="px-2.5 py-1 rounded text-xs transition-all"
                      style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)' }}
                    >
                      {t('admin.notificationsRules.edit', 'Edit')}
                    </button>
                    <button
                      onClick={() => handleDelete(rule?.id)}
                      disabled={deletingId === rule?.id}
                      className="px-2.5 py-1 rounded text-xs transition-all"
                      style={{ backgroundColor: 'rgba(239,68,68,0.1)', color: '#ef4444' }}
                    >
                      {deletingId === rule?.id ? '...' : '✕'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}

      {filteredCategories.length === 0 && (
        <div className="rounded-xl border p-12 flex flex-col items-center gap-3"
          style={{ backgroundColor: '#0d0d0d', borderColor: '#1f1f1f' }}>
          <span className="text-4xl">⚙️</span>
          <p className="text-white/40 text-sm">Aucune règle dans cette catégorie</p>
        </div>
      )}
    </div>
  );
};

export default RulesTable;
